/**
 * Shared account-scoped fetch helper — the same `getApiUrl`/`authHeaders`/
 * `request<T>` boilerplate that ClientService.ts wrote inline. Extracted here
 * once a THIRD account-level service (ClientQuoteService, ClientInvoiceService)
 * needed the identical block, rather than risk drifting three copies of the
 * same auth logic. ClientService.ts itself is left untouched — it already
 * works and isn't part of this change.
 */
import { STORAGE_KEYS } from '../constants/storageKeys';
import { ApiError, buildApiError } from '../utils/apiErrors';
import { endSessionIfExpired } from '../utils/session';

function getApiUrl(): string {
  return process.env.REACT_APP_GG_API_URL || 'http://localhost:3000';
}

function getApiKey(): string {
  return (import.meta.env.VITE_ANS_WEB_API_KEY as string | undefined) || '';
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'x-api-key': getApiKey() };
  const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export async function accountRequest<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const { method = 'GET', body } = options;
  const sentToken = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: body != null ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw ApiError.offline();
  }

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    endSessionIfExpired(res.status, sentToken);
    throw buildApiError(res.status, payload);
  }

  return payload as T;
}

/** Fetches a binary response (e.g. a generated PDF) and triggers a browser
 *  download, matching the anchor-click-then-revoke pattern already used for
 *  client-side CSV exports elsewhere in this app (see QuotesTracker.tsx). */
export async function downloadAccountFile(path: string, filename: string): Promise<void> {
  const sentToken = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}${path}`, {
      method: 'GET',
      headers: authHeaders(),
    });
  } catch {
    throw ApiError.offline();
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // Non-JSON error body: the status alone decides the wording.
    }
    endSessionIfExpired(res.status, sentToken);
    throw buildApiError(res.status, body);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
